import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import { sliderBottom } from "d3-simple-slider";
import Loading from "../../common/components/loading/loading";
import {
  appendSignedVerticalAreaGradients,
  appendTimeSeriesTooltipOverlay,
  buildTooltipHtml,
  createTooltipHandlers,
  createZeroAnchoredArea,
  dashboardSeriesColors,
  formatTooltipCurrency,
  formatTooltipDate,
  getNumericExtentIncludingZero,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";

const NaturalGasPoolPriceLineGraph = ({
  gasData,
  poolPriceData,
  isLoading,
  initialStartDate,
  initialEndDate,
  title = "AESO 7x24 vs ICE Spot Natural Gas",
}) => {
  const svgRef = useRef();
  const tooltipRef = useRef(null);
  const sliderRef = useRef();

  const renderChart = useCallback(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    removeTooltip(tooltipRef);

    if (!svg.node()) {
      return;
    }

    const parentContainer = svg.node().parentNode;
    const parentWidth = parentContainer.clientWidth;
    const parentHeight = parentContainer.clientHeight - 20;
    const margin = { top: 30, right: 60, bottom: 70, left: 50 };

    const innerWidth = parentWidth - margin.left - margin.right;
    const innerHeight = parentHeight - margin.top - margin.bottom;

    if (!innerHeight || innerHeight <= 0 || !innerWidth) return;

    const chartSvg = svg
      .attr("width", parentWidth)
      .attr("height", parentHeight)
      .attr("viewBox", `0 0 ${parentWidth} ${parentHeight}`)
      .attr("preserveAspectRatio", "none")
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const formatMonth = d3.timeFormat("%Y-%m");
    const poolColor = dashboardSeriesColors.accentBlue;
    const gasColor = dashboardSeriesColors.accentOrange;

    const averageByMonth = (data, dateField, valueField, termName) => {
      const groupedSeries = d3.group(data, (d) => {
        const parsedDate = new Date(d[dateField]);
        if (!parsedDate || d[dateField] === null) {
          console.error(`Failed to parse date: ${d[dateField]}`);
          return null;
        }
        return formatMonth(parsedDate);
      });

      return Array.from(groupedSeries, ([key, values]) => {
        if (key === null) return null;
        const avg = d3.mean(values, (d) => {
          const value = +d[valueField];
          if (isNaN(value)) {
            console.error(`Invalid ${valueField} value:`, d[valueField]);
            return null;
          }
          return value;
        });

        return {
          marketDate: new Date(key),
          [valueField]: avg,
          termName,
        };
      }).filter((d) => d !== null);
    };

    const averagedGasData = averageByMonth(
      gasData,
      "marketDate",
      "settlePrice",
      "Gas Price",
    );

    const minDate = new Date(
      Math.min(...averagedGasData.map((x) => x.marketDate)),
    );

    const averagedPoolPriceData = averageByMonth(
      poolPriceData.filter((x) => new Date(x.marketDate) >= minDate),
      "marketDate",
      "poolPrice",
      "Pool Price",
    );

    const combinedData = [...averagedGasData, ...averagedPoolPriceData].sort(
      (a, b) => a.marketDate - b.marketDate,
    );
    const groupedData = d3.group(combinedData, (d) => d.termName);

    const xScale = d3
      .scaleTime()
      .domain(d3.extent(combinedData, (d) => d.marketDate))
      .range([0, innerWidth]);

    const yScalePool = d3
      .scaleLinear()
      .domain(
        getNumericExtentIncludingZero(
          averagedPoolPriceData.map((d) => d.poolPrice),
        ),
      )
      .nice()
      .range([innerHeight, 0]);

    const yScaleGas = d3
      .scaleLinear()
      .domain(
        getNumericExtentIncludingZero(averagedGasData.map((d) => d.settlePrice)),
      )
      .nice()
      .range([innerHeight, 0]);

    const linePool = d3
      .line()
      .defined((d) => Number.isFinite(d.poolPrice))
      .x((d) => xScale(d.marketDate))
      .y((d) => yScalePool(d.poolPrice))
      .curve(d3.curveMonotoneX);

    const lineGas = d3
      .line()
      .defined((d) => Number.isFinite(d.settlePrice))
      .x((d) => xScale(d.marketDate))
      .y((d) => yScaleGas(d.settlePrice))
      .curve(d3.curveMonotoneX);

    const poolAreas = {
      positive: createZeroAnchoredArea({
        valueAccessor: (d) => d.poolPrice,
        xAccessor: (d) => d.marketDate,
        xScale,
        yScale: yScalePool,
      }),
      negative: createZeroAnchoredArea({
        polarity: "negative",
        valueAccessor: (d) => d.poolPrice,
        xAccessor: (d) => d.marketDate,
        xScale,
        yScale: yScalePool,
      }),
    };

    const gasAreas = {
      positive: createZeroAnchoredArea({
        valueAccessor: (d) => d.settlePrice,
        xAccessor: (d) => d.marketDate,
        xScale,
        yScale: yScaleGas,
      }),
      negative: createZeroAnchoredArea({
        polarity: "negative",
        valueAccessor: (d) => d.settlePrice,
        xAccessor: (d) => d.marketDate,
        xScale,
        yScale: yScaleGas,
      }),
    };

    chartSvg
      .append("g")
      .attr("class", "x-axis d3-chart-axis")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(d3.timeMonth.every(3))
          .tickFormat(d3.timeFormat("%b %Y")),
      )
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end");

    const yAxisPool = chartSvg
      .append("g")
      .attr("class", "y-axis y-axis-pool d3-chart-axis")
      .call(d3.axisLeft(yScalePool).tickFormat((d) => `$${d.toFixed(2)}`));

    const yAxisGas = chartSvg
      .append("g")
      .attr("class", "y-axis y-axis-gas d3-chart-axis")
      .attr("transform", `translate(${innerWidth},0)`)
      .call(d3.axisRight(yScaleGas).tickFormat((d) => `$${d.toFixed(2)}`));

    const gridlines = chartSvg
      .append("g")
      .attr("class", "grid d3-chart-gridline")
      .call(d3.axisLeft(yScalePool).tickSize(-innerWidth).tickFormat(""))
      .attr("stroke-opacity", 0.1);

    const poolGradients = appendSignedVerticalAreaGradients({
      chartSvg,
      idPrefix: "natural-gas-pool-area",
      color: poolColor,
    });
    const gasGradients = appendSignedVerticalAreaGradients({
      chartSvg,
      idPrefix: "natural-gas-spot-area",
      color: gasColor,
    });

    const areaLayer = chartSvg.append("g").attr("class", "dashboard-area-layer");
    const areaPaths = new Map();

    [
      {
        label: "Gas Price",
        data: averagedGasData,
        areas: gasAreas,
        gradients: gasGradients,
      },
      {
        label: "Pool Price",
        data: averagedPoolPriceData,
        areas: poolAreas,
        gradients: poolGradients,
      },
    ].forEach((series) => {
      areaPaths.set(series.label, {
        negative: areaLayer
          .append("path")
          .datum(series.data)
          .attr("class", "d3-chart-area dashboard-area-highlight")
          .attr("fill", `url(#${series.gradients.negativeGradientId})`)
          .attr("d", series.areas.negative),
        positive: areaLayer
          .append("path")
          .datum(series.data)
          .attr("class", "d3-chart-area dashboard-area-highlight")
          .attr("fill", `url(#${series.gradients.positiveGradientId})`)
          .attr("d", series.areas.positive),
      });
    });

    const linesGroup = chartSvg.append("g").attr("class", "lines");

        [
      { label: "Gas Price", stroke: gasColor, strokeWidth: 2 },
      { label: "Pool Price", stroke: poolColor, strokeWidth: 2.4 },
    ].forEach((series) => {
      linesGroup
        .append("path")
        .datum(groupedData.get(series.label) ?? [])
        .attr("class", "line")
        .attr("fill", "none")
        .attr("stroke", series.stroke)
        .attr("stroke-width", series.strokeWidth)
        .attr("d", series.label === "Pool Price" ? linePool : lineGas);
    });

    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });
    let currentPoolSeries = averagedPoolPriceData;
    let currentGasSeries = averagedGasData;
    let tooltipDates = Array.from(
      new Set(combinedData.map((d) => +d.marketDate)),
      (value) => ({ marketDate: new Date(value) }),
    ).sort((a, b) => a.marketDate - b.marketDate);

    appendTimeSeriesTooltipOverlay({
      chartSvg,
      innerWidth,
      innerHeight,
      xScale,
      getData: () => tooltipDates,
      xAccessor: (d) => d.marketDate,
      showTooltip,
      hideTooltip,
      htmlAccessor: (d) => {
        const targetKey = +d.marketDate;
        const poolPoint = currentPoolSeries.find((point) => +point.marketDate === targetKey);
        const gasPoint = currentGasSeries.find((point) => +point.marketDate === targetKey);

        return buildTooltipHtml({
          title: formatTooltipDate(d.marketDate, "%Y-%m-%d"),
          rows: [
            {
              label: "Pool Price",
              value: formatTooltipCurrency(poolPoint?.poolPrice, 2),
              color: poolColor,
            },
            {
              label: "Gas Price",
              value: formatTooltipCurrency(gasPoint?.settlePrice, 2),
              color: gasColor,
            },
          ],
        });
      },
    });

    chartSvg
      .append("text")
      .attr("x", innerWidth / 2)
      .attr("y", -margin.top / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .text(title)
      .attr("class", "x-axis d3-chart-title");

    chartSvg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left)
      .attr("x", 0 - innerHeight / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .attr("font-size", "14px")
      .text("Pool Price ($/MWh)")
      .attr("class", "d3-chart-axis-label");

    chartSvg
      .append("text")
      .attr("transform", "rotate(90)")
      .attr("y", -(innerWidth + margin.right - 5))
      .attr("x", innerHeight / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .attr("font-size", "14px")
      .text("Gas Price ($/GJ)")
      .attr("class", "d3-chart-axis-label");

    const legendSpacing = 100;
    const legendLineLength = 20;
    const legendKeys = ["Pool Price", "Gas Price"];

    const legend = chartSvg
      .append("g")
      .attr("font-family", "sans-serif")
      .attr("font-size", 10)
      .attr("text-anchor", "middle")
      .attr(
        "transform",
        `translate(${innerWidth / 2}, ${-margin.top / 2 + 10})`,
      )
      .selectAll("g")
      .data(legendKeys)
      .enter()
      .append("g")
      .attr(
        "transform",
        (d, i) =>
          `translate(${i * legendSpacing - ((legendKeys.length - 1) * legendSpacing) / 2}, 0)`,
      );

    legend
      .append("line")
      .attr("x1", -legendLineLength / 2)
      .attr("x2", legendLineLength / 2)
      .attr("stroke", (d) => (d === "Pool Price" ? poolColor : gasColor))
      .attr("stroke-width", (d) => (d === "Pool Price" ? 2.4 : 2));

    legend
      .append("text")
      .attr("x", legendLineLength * 2 - 5)
      .attr("y", 3)
      .attr("class", "d3-chart-legend-label")
      .text((d) => d);

    const sliderSvg = d3
      .select(sliderRef.current)
      .attr("width", parentWidth)
      .attr("height", 40)
      .attr("class", "no-print")
      .attr("transform", "translate(0,-20)");

    sliderSvg.selectAll("*").remove();

    const sliderWidth = innerWidth;

    const updateChart = (range) => {
      const [startDate, endDate] = range;

      const filteredData = Array.from(groupedData).map(([key, values]) => [
        key,
        values.filter(
          (d) => d.marketDate >= startDate && d.marketDate <= endDate,
        ),
      ]);

      const filteredPoolSeries =
        filteredData.find(([key]) => key === "Pool Price")?.[1] ?? [];
      const filteredGasSeries =
        filteredData.find(([key]) => key === "Gas Price")?.[1] ?? [];

      currentPoolSeries = filteredPoolSeries;
      currentGasSeries = filteredGasSeries;
      tooltipDates = Array.from(
        new Set([...filteredPoolSeries, ...filteredGasSeries].map((d) => +d.marketDate)),
        (value) => ({ marketDate: new Date(value) }),
      ).sort((a, b) => a.marketDate - b.marketDate);

      xScale.domain([startDate, endDate]);

      yScalePool
        .domain(
          getNumericExtentIncludingZero(
            filteredPoolSeries.map((d) => d.poolPrice),
          ),
        )
        .nice();
      yScaleGas
        .domain(
          getNumericExtentIncludingZero(
            filteredGasSeries.map((d) => d.settlePrice),
          ),
        )
        .nice();

      const t = d3.transition().duration(50).ease(d3.easeLinear);

      chartSvg
        .select(".x-axis")
        .transition(t)
        .call(
          d3
            .axisBottom(xScale)
            .ticks(d3.timeMonth.every(3))
            .tickFormat(d3.timeFormat("%b %Y")),
        )
        .selectAll("text")
        .attr("transform", "rotate(-45)")
        .style("text-anchor", "end")
        .style("fill", "grey");

      yAxisPool
        .transition(t)
        .call(d3.axisLeft(yScalePool).tickFormat((d) => `$${d.toFixed(0)}`));

      yAxisGas
        .transition(t)
        .call(d3.axisRight(yScaleGas).tickFormat((d) => `$${d.toFixed(2)}`));

      gridlines
        .transition(t)
        .call(d3.axisLeft(yScalePool).tickSize(-innerWidth).tickFormat(""));

      areaPaths.get("Pool Price").positive
        .datum(filteredPoolSeries)
        .transition(t)
        .attr("d", poolAreas.positive);
      areaPaths.get("Pool Price").negative
        .datum(filteredPoolSeries)
        .transition(t)
        .attr("d", poolAreas.negative);
      areaPaths.get("Gas Price").positive
        .datum(filteredGasSeries)
        .transition(t)
        .attr("d", gasAreas.positive);
      areaPaths.get("Gas Price").negative
        .datum(filteredGasSeries)
        .transition(t)
        .attr("d", gasAreas.negative);

      linesGroup
        .selectAll(".line")
        .data(filteredData)
        .join("path")
        .attr("class", "line")
        .attr("fill", "none")
        .attr("stroke", ([key]) => (key === "Pool Price" ? poolColor : gasColor))
        .attr("stroke-width", ([key]) => (key === "Pool Price" ? 2.4 : 2))
        .transition(t)
        .attr("d", ([key, values]) => {
          if (key === "Pool Price") {
            return linePool(values);
          }
          if (key === "Gas Price") {
            return lineGas(values);
          }
          return null;
        });
    };

    const throttledUpdateChart = throttle(updateChart, 30);

    const slider = sliderBottom()
      .min(d3.min(combinedData, (d) => d.marketDate))
      .max(d3.max(combinedData, (d) => d.marketDate))
      .width(sliderWidth)
      .tickFormat(d3.timeFormat("%Y-%m-%d"))
      .ticks(5)
      .default([
        initialStartDate || d3.min(combinedData, (d) => d.marketDate),
        initialEndDate || d3.max(combinedData, (d) => d.marketDate),
      ])
      .fill("var(--theme-interactive-element)")
      .on("onchange", throttledUpdateChart);

    sliderSvg
      .append("g")
      .attr("transform", `translate(${(parentWidth - sliderWidth) / 2},0)`)
      .call(slider);

    sliderSvg.selectAll(".slider text").style("fill", "grey");
    sliderSvg.selectAll(".tick text").style("opacity", "0");

    updateChart([
      initialStartDate || d3.min(combinedData, (d) => d.marketDate),
      initialEndDate || d3.max(combinedData, (d) => d.marketDate),
    ]);
  }, [gasData, poolPriceData, initialStartDate, initialEndDate, title]);

  const throttle = (func, limit) => {
    let lastFunc;
    let lastRan;
    return function () {
      const context = this;
      const args = arguments;
      if (!lastRan) {
        func.apply(context, args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(
          function () {
            if (Date.now() - lastRan >= limit) {
              func.apply(context, args);
              lastRan = Date.now();
            }
          },
          limit - (Date.now() - lastRan),
        );
      }
    };
  };

  useEffect(() => {
    if (!isLoading && gasData && poolPriceData) {
      renderChart();
    }
  }, [renderChart, isLoading, gasData, poolPriceData]);

  useEffect(() => {
    let resizeTimeout = null;

    const svgNode = svgRef.current;
    if (!svgNode) return;

    const throttledResize = () => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          if (!isLoading && gasData && poolPriceData) {
            renderChart();
          }
          resizeTimeout = null;
        }, 100);
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (svgNode) {
      observer.observe(svgNode.parentNode);
    }

    return () => {
      if (svgNode && svgNode.parentNode) {
        observer.unobserve(svgNode.parentNode);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart, isLoading, gasData, poolPriceData]);

  if (isLoading ?? true) return <Loading />;

  return (
    <div className="dashboard-d3-chart" style={{ width: "100%", height: "100%" }}>
      <svg ref={svgRef} style={{ width: "100%" }}></svg>
      <svg ref={sliderRef} style={{ width: "100%" }}></svg>
    </div>
  );
};

export default NaturalGasPoolPriceLineGraph;








